import { Capacitor } from '@capacitor/core';
import {
	RiArrowDropDownLine,
	RiArrowDropUpLine,
	RiCheckDoubleFill,
	RiCloseFill,
	RiFlashlightFill,
	RiInformationLine,
} from '@remixicon/react';
import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Warning } from '../../components/Icons/Warning';
import ModalEnroll from '../../components/Modal/Enroll';
import ModalPick from '../../components/Modal/Picks';
import Tooltip from '../../components/Tooltip';
import TooltipPick from '../../components/Tooltip/tooltipPick';
import Button from '../../components/ui/button';
import Toast from '../../components/ui/toast';
import {
	choosePlatform,
	CurrencyFormatter,
	sentryCapture,
	showWalletAndBalance,
	transformInitial,
	verifyDatePick,
} from '../../helpers/utils';
import { useApi } from '../../hooks/useApi';
import { useBalanceStore } from '../../store/balance';
import { useModalBalanceAlertStore } from '../../store/modalBalanceAlert';
import { useModalLoginStore } from '../../store/modalLogin';
import { useTokenStore } from '../../store/token';
import { SentryError } from '../../types/enums';
import { FeedPickProps } from '../../types/pickFeed';
import { useAuthContext } from '../Auth/AuthContext';
import { Multiple } from './multiple';
import { validateSelectionCount } from './validateSelectionCount';

interface PicksContextType {
	picks: any;
	SetPicks: (data: any) => void;
	ClearPicks: () => void;
	tournamentGroup: any;
	SetTournamentGroup: (data: any) => void;
	ClearTournamentGroup: () => void;
}

export const PicksContext = createContext<PicksContextType | undefined>(
	undefined
);

interface PicksProviderProps {
	children: ReactNode;
}

const checkoutShow = ['/', '/home'];

export const PicksProvider: React.FC<PicksProviderProps> = ({ children }) => {
	const api = useApi();
	const location = useLocation();
	const { user } = useAuthContext();
	const navigate = useNavigate();
	const currentBalance = useBalanceStore((state) => state.balance);
	const token = useTokenStore((state: any) => state.token);
	const { modal, openModal, closeModal, enrollsInfoModal } =
		useModalBalanceAlertStore();
	const { openModalLogin } = useModalLoginStore();

	const [picks, setPicks] = useState<any[]>([]);
	const [tournamentGroup, setTournamentGroup] =
		useState<FeedPickProps | null>(null);
	const [proposalTournaments, setProposalTournaments] = useState<any[]>([]);

	const [changedModal, setChangedModal] = useState(false);
	const [sucessModal, setSucessModal] = useState(false);
	const [loadingProposal, setLoadingProposal] = useState(false);
	const [checkout, setCheckout] = useState(true);
	const [disableButton, setDisableButton] = useState(true);

	const [numberOfRegistrations, setNumberOfRegistrations] = useState(5);
	const [error, setError] = useState('');
	const [errorProposal, setErrorProposal] = useState('');

	const tiers = Array.from(
		new Set(
			tournamentGroup?.tournaments
				.map((t) => Number(t.tier))
				.filter((n) => !isNaN(n))
		)
	).sort((a, b) => a - b);
	const minimumPickSelection =
		(tournamentGroup &&
			tournamentGroup.tournaments
				.map((item) => Number(item.tier))
				.reduce((min, atual) => Math.min(min, atual), Infinity)) ||
		0;
	const maxPickSelection =
		(tournamentGroup &&
			tournamentGroup.tournaments
				.map((item) => Number(item.tier))
				.reduce((max, atual) => Math.max(max, atual), -Infinity)) ||
		0;
	const picksCount = picks.length;
	const factors = picks.map((item) => item.pick.factor);
	const factor =
		Math.trunc(factors.reduce((total, factor) => total * factor, 1) * 100) /
		100;
	const totalPrizeAmt = proposalTournaments.reduce((acc, pick) => {
		const tournament = tournamentGroup?.tournaments.find(
			(t) => t.id === pick.tournamentPickId
		);
		const prize = Number(tournament?.prizeAmt) || 0;
		return acc + prize;
	}, 0);

	const SetPicks = (item: any) => {
		if (!user) {
			setChangedModal(false);
			openModalLogin(true, `/`);
			return;
		}

		const playerAlreadyPicked = picks.some(
			(pick: any) => pick.pick.playerId === item.pick.playerId
		);
		const existingPlayerPick = picks.find(
			(pick: any) =>
				pick.pick.playerId === item.pick.playerId &&
				pick.pick.type === item.pick.type &&
				pick.tournamentGroupId === item.tournamentGroupId &&
				pick.pick.scoutId === item.pick.scoutId &&
				pick.pick.teamId === item.pick.teamId
		);
		const isSamePick =
			existingPlayerPick &&
			existingPlayerPick.pickSelection === item.pickSelection;

		if (isSamePick) {
			const updatedPicks = picks.filter(
				(pick: any) =>
					!(
						pick.pick.playerId === item.pick.playerId &&
						pick.pick.type === item.pick.type &&
						pick.tournamentGroupId === item.tournamentGroupId &&
						pick.pick.scoutId === item.pick.scoutId &&
						pick.pick.teamId === item.pick.teamId &&
						pick.pickSelection === item.pickSelection
					)
			);
			setPicks(updatedPicks);
			return;
		}
		if (existingPlayerPick) {
			const updatedPicks = picks.map((pick: any) =>
				pick.pick.playerId === item.pick.playerId &&
				pick.pick.type === item.pick.type &&
				pick.tournamentGroupId === item.tournamentGroupId &&
				pick.pick.scoutId === item.pick.scoutId &&
				pick.pick.teamId === item.pick.teamId
					? item
					: pick
			);
			setPicks(updatedPicks);
			return;
		}
		if (playerAlreadyPicked) {
			return toast(
				<Toast variant="warning">
					Já existe uma pick para o jogador {item.player.name}!
				</Toast>
			);
		}
		if (picksCount >= maxPickSelection) {
			return toast(
				<Toast variant="error">Limite máximo de picks atingido</Toast>
			);
		}

		setPicks([...picks, item]);
	};
	const ClearPicks = () => {
		setPicks([]);
		setChangedModal(false);
		setError('');
		setDisableButton(true);
	};
	const RemovePick = (item: any) => {
		const existingPlayerPick = picks.find(
			(pick: any) =>
				pick.pick.playerId === item.pick.playerId &&
				pick.pick.type === item.pick.type &&
				pick.tournamentGroupId === item.tournamentGroupId &&
				pick.pick.scoutId === item.pick.scoutId &&
				pick.pick.teamId === item.pick.teamId
		);

		const isSamePick =
			existingPlayerPick &&
			existingPlayerPick.pickSelection === item.pickSelection;
		if (isSamePick) {
			const updatedPicks = picks.filter(
				(pick: any) =>
					!(
						pick.pick.playerId === item.pick.playerId &&
						pick.pick.type === item.pick.type &&
						pick.tournamentGroupId === item.tournamentGroupId &&
						pick.pick.scoutId === item.pick.scoutId &&
						pick.pick.teamId === item.pick.teamId &&
						pick.pickSelection === item.pickSelection
					)
			);
			setPicks(updatedPicks);

			if (updatedPicks.length === 0) {
				setChangedModal(false);
			}
		}
	};

	const UpdatePicks = (item: any) => {
		const playerAlreadyPicked = picks.some(
			(pick: any) => pick.pick.playerId === item.pick.playerId
		);

		if (playerAlreadyPicked) {
			const existingPlayerPick = picks.find(
				(pick: any) =>
					pick.pick.playerId === item.pick.playerId &&
					pick.pick.type === item.pick.type &&
					pick.tournamentGroupId === item.tournamentGroupId &&
					pick.pick.scoutId === item.pick.scoutId &&
					pick.pick.teamId === item.pick.teamId
			);
			const isSamePick =
				existingPlayerPick &&
				existingPlayerPick.pickSelection !== item.pickSelection;

			if (isSamePick) {
				const updatedPicks = picks.map((pick: any) =>
					pick.pick.playerId === item.pick.playerId &&
					pick.pick.type === item.pick.type &&
					pick.tournamentGroupId === item.tournamentGroupId &&
					pick.pick.scoutId === item.pick.scoutId &&
					pick.pick.teamId === item.pick.teamId
						? item
						: pick
				);
				setPicks(updatedPicks);
			}
		}
	};

	const SetTournamentGroup = (item: any) => {
		setTournamentGroup(item);
	};
	const ClearTournamentGroup = () => {
		setTournamentGroup(null);
	};

	const removeEntryEnroll = () => {
		if (numberOfRegistrations <= 0) {
			return;
		}
		setNumberOfRegistrations(numberOfRegistrations - 1);
	};
	const addEntryEnroll = () => {
		setNumberOfRegistrations(numberOfRegistrations + 1);
	};

	const findProposal = async () => {
		if (!tournamentGroup) {
			return;
		}
		try {
			setLoadingProposal(true);
			const body = {
				tournamentGroupId: tournamentGroup.id,
				entryAmount: Number(
					Number(tournamentGroup.entryAmt) * numberOfRegistrations
				),
				picks: picksCount,
			};
			const response = await api.getPickProposal(body, token);

			if (!response.success) {
				setErrorProposal(response.message);
				return;
			}
			setErrorProposal('');
			setProposalTournaments(response.proposal);
			setNumberOfRegistrations(response.totalEntries);
		} catch (error: any) {
			setErrorProposal(error.message);
			toast(<Toast variant="error">{error.message}</Toast>);
			sentryCapture(error, 'findProposal', SentryError.Error);
		} finally {
			setLoadingProposal(false);
		}
	};

	const [reset, setReset] = useState(false);

	useEffect(() => {
		if (!tournamentGroup || reset) {
			return;
		}

		const valide = validateSelectionCount(tiers, picksCount);

		if (valide) {
			setError(valide);
			setDisableButton(true);
		} else {
			findProposal();
			setError('');
			setDisableButton(false);
		}
	}, [picksCount, numberOfRegistrations]);

	useEffect(() => {
		if (checkoutShow.includes(location.pathname)) {
			setCheckout(true);
		} else {
			if (location.state?.background) {
			} else {
				setCheckout(false);
			}
		}
	}, [location.pathname]);

	const handleEnrollPick = async () => {
		if (!tournamentGroup) {
			return;
		}
		if (
			parseFloat(currentBalance) <
			Number(tournamentGroup.entryAmt) * numberOfRegistrations
		) {
			if (showWalletAndBalance()) {
				openModal(true);
				setChangedModal(false);
				return;
			}
		}
		try {
			const body = {
				tournamentGroupId: tournamentGroup.id,
				entryAmount: Number(
					Number(tournamentGroup.entryAmt) * numberOfRegistrations
				),
				picks: picks,
				tournamentPicks: proposalTournaments,
			};
			setLoadingProposal(true);
			const res = await api.createEnrollPick(body, token);
			if (!res?.success) {
				throw new Error(res.message);
			}

			setReset(true);
			setSucessModal(true);
		} catch (error: any) {
			toast(<Toast variant="error">{error.message}</Toast>);
			sentryCapture(error, 'handleEnrollPick', SentryError.Error);
		} finally {
			setLoadingProposal(false);
		}
	};

	const verifyPickEntry = (pick: any): boolean => {
		const pickEntry = picks.find(
			(pickEntry: any) =>
				pickEntry.tournamentGroupId === pick.tournamentGroupId &&
				pickEntry.pick.playerId === pick.pick.playerId &&
				pickEntry.pick.scoutId === pick.pick.scoutId &&
				pickEntry.pick.teamId === pick.pick.teamId &&
				pickEntry.pick.type === pick.pick.type &&
				pickEntry.pickSelection === pick.pickSelection
		);
		return !!pickEntry;
	};

	const clearAllState = () => {
		setPicks([]);
		setProposalTournaments([]);
		setNumberOfRegistrations(1);
		setChangedModal(false);
		setReset(false);
	};

	const closeSucessModalEnroll = () => {
		setSucessModal(false);
		clearAllState();
	};

	const findEventId = (id: string) => {
		return tournamentGroup?.events.find((event) => event.eventId === id);
	};

	return (
		<PicksContext.Provider
			value={{
				picks,
				SetPicks,
				ClearPicks,
				tournamentGroup,
				SetTournamentGroup,
				ClearTournamentGroup,
			}}
		>
			{children}

			{((user?.permisson === 'admin' && checkout) ||
				(choosePlatform() && checkout)) && (
				<div
					className={
						'fixed flex items-start justify-start left-0 right-0 rounded-t-md w-full duration-500 transform ' +
						'lg:max-w-screen-xl lg:mx-auto pr-3.5 pl-3.5 lg:z-40 sm:pr-3 sm:z-40 ' +
						'bg-custom-gray4 ' +
						(Capacitor.getPlatform() === 'ios'
							? ' bottom-[70px]'
							: ' bottom-[55px]')
					}
				>
					{picks.length > 0 ? (
						<div className="flex flex-row gap-1 justify-between items-center w-full h-full rounded-t-md py-1">
							<div className="flex justify-between items-start flex-col gap-1">
								<div className="flex flex-row items-center relative">
									{picks && picks.length > 0 ? (
										<>
											{picks.map((p, i) => (
												<div
													key={i}
													className="flex flex-row items-center justify-center w-12 h-7 max-[375px]:w-9 max-[375px]:h-7 -ml-1 relative z-10"
												>
													{p.team.image ? (
														<img
															className="w-7 h-7 max-[375px]:w-6 max-[375px]:h-6 object-cover -mr-3"
															src={p.team.image}
														/>
													) : (
														<div className="w-7 h-7 max-[375px]:w-6 max-[375px]:h-6 rounded-full -mr-2 text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
															{transformInitial(
																p.team.name
															)}
														</div>
													)}
													{p.player.image ? (
														<img
															className="w-7 h-7 max-[375px]:w-6 max-[375px]:h-6 rounded-[33.55px] object-cover"
															src={p.player.image}
														/>
													) : (
														<div className="w-7 h-7 max-[375px]:w-6 max-[375px]:h-6 relative rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
															{transformInitial(
																p.player.name
															)}
														</div>
													)}
												</div>
											))}
											{Array.from({
												length: Math.max(
													0,
													maxPickSelection -
														picks.length
												),
											}).map((_, i) => (
												<div
													key={`empty-${i}`}
													className="w-7 h-7 -ml-1"
												>
													<div className="w-7 h-7 rounded-full border border-dashed border-black text-black font-bahnschrift text-xs bg-transparent items-center justify-center flex">
														+
													</div>
												</div>
											))}
										</>
									) : (
										Array.from({
											length: minimumPickSelection,
										}).map((_, i) => (
											<div
												key={`empty-${i}`}
												className="w-7 h-7 -ml-1"
											>
												<div className="w-7 h-7 rounded-full border border-dashed border-black text-black font-bahnschrift text-xs bg-transparent items-center justify-center flex">
													+
												</div>
											</div>
										))
									)}
								</div>
								<div className="flex flex-col">
									<div className="flex justify-between">
										<span className="text-text/heading-default max-[415px]:text-xs text-xs text-left font-normal font-bahnschrift leading-[16px]">
											Qtd. Inscrições:{' '}
											{numberOfRegistrations}
										</span>
										<span className="text-text/heading-default max-[415px]:text-xs text-xs text-left font-normal font-bahnschrift leading-[16px]">
											Total: R${' '}
											{CurrencyFormatter(
												Number(
													tournamentGroup?.entryAmt
												) * numberOfRegistrations
											)}
										</span>
									</div>
									<div className="flex flex-row justify-start items-start gap-1">
										{[1, 2, 5, 10, 20, 50].map((q, i) => (
											<div
												key={i}
												onClick={() => {
													setNumberOfRegistrations(q);
												}}
												className={
													'w-7 h-7 cursor-pointer rounded-full border border-custom-border font-bahnschrift text-xs items-center justify-center flex' +
													(numberOfRegistrations === q
														? ' bg-primary text-white'
														: ' text-text-paragraph')
												}
											>
												{q}
											</div>
										))}
									</div>
								</div>
							</div>

							<div className="flex flex-col gap-1 justify-between items-end -ml-1">
								<div className="flex flex-col gap-1">
									{picks.length >= minimumPickSelection ? (
										<>
											<div className="flex flex-row justify-between items-center gap-2">
												<div className="flex flex-row items-center gap-1">
													{factor !== 1 && (
														<>
															<span className="text-text/heading-default text-[10px] font-normal font-bahnschrift leading-3">
																x{factor}
															</span>

															<Tooltip
																icon={
																	<RiInformationLine
																		color="#636363"
																		className="w-4 h-4"
																	/>
																}
																infoIcon="warning"
																title="Multiplicador"
																message={
																	<Multiple />
																}
															/>
														</>
													)}
												</div>

												<div className="flex flex-row items-center gap-1">
													<span className="text-text/heading-default text-[10px] font-normal font-bahnschrift leading-3">
														Torneios:{' '}
														{
															proposalTournaments.length
														}
													</span>
													<TooltipPick
														icon={
															<RiInformationLine
																color="#636363"
																className="w-4 h-4"
															/>
														}
														tournamentGroup={
															tournamentGroup
														}
														proposal={
															proposalTournaments
														}
													/>
												</div>
											</div>
											<div className="text-text/heading-default text-[11px] font-normal font-bahnschrift leading-3">
												Prêmio Máx: R${' '}
												{CurrencyFormatter(
													Number(totalPrizeAmt)
												)}
											</div>
										</>
									) : (
										<div className="h-8"></div>
									)}
								</div>
								<div className="flex flex-col items-center justify-center w-full h-full">
									<Button
										className="w-full py-1.5 px-1.5"
										onClick={() => {
											setChangedModal(true);
										}}
										textCustom="text-[14px]"
										disabled={
											disableButton || loadingProposal
										}
									>
										{error ? error : 'Concluir'}
									</Button>
								</div>
							</div>
						</div>
					) : (
						<div className="flex flex-row items-center justify-center w-full h-full p-2">
							<div className="flex justify-center items-center">
								<RiArrowDropUpLine className="w-6 text-white" />
								<RiArrowDropDownLine className="w-6 text-white -ml-3" />
							</div>
							<span className="text-white text-sm font-bahnschrift font-semibold">
								Selecione {minimumPickSelection} ou mais picks
							</span>
						</div>
					)}
				</div>
			)}
			{((user?.permisson === 'admin' && user && tournamentGroup) ||
				(choosePlatform() && user && tournamentGroup)) && (
				<>
					<ModalPick
						id="modalPick"
						open={changedModal}
						outsideClick
						onClose={() => setChangedModal(false)}
						title="Escolhas de Picks"
					>
						<div className="flex flex-col gap-6">
							<div className="flex flex-col">
								<div className="grid grid-cols-1 gap-1 max-h-[50vh] overflow-auto relative">
									{picks.map((p, i) => {
										const pickSelectUp = {
											...p,
											pickSelection: 'up',
										};
										const pickSelectDown = {
											...p,
											pickSelection: 'down',
										};
										return (
											<div
												key={i}
												className="flex flex-row justify-between items-center bg-surface-action-secondary rounded-[5px] p-1.5"
											>
												<div className="flex flex-row gap-0.5 items-center justify-center">
													{p.player.image ? (
														<img
															className="w-8 h-8 rounded-[33.55px] object-cover"
															src={p.player.image}
														/>
													) : (
														<div className="w-8 h-8 rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
															{transformInitial(
																p.player.name
															)}
														</div>
													)}

													<div className="flex flex-col">
														<div className="flex justify-start items-center gap-0.5">
															{p.team.image ? (
																<img
																	className="w-5 h-5 object-cover"
																	src={
																		p.team
																			.image
																	}
																/>
															) : (
																<div className="w-5 h-5 rounded-full text-white font-bahnschrift text-xs bg-primary items-center justify-center flex">
																	{transformInitial(
																		p.team
																			.name
																	)}
																</div>
															)}

															<span className="text-text/heading-default text-sm text-center font-normal font-bahnschrift">
																{p.player.name}
																<span className="text-custom-gray4 pl-1 text-xs text-center font-normal font-bahnschrift">
																	{
																		p.player
																			.position
																	}
																</span>
															</span>
														</div>

														<div className="flex justify-start items-center gap-0.5 pl-1.5">
															<div className="flex justify-start items-start gap-1 text-text/heading-default text-xs text-center font-normal font-bahnschrift">
																{findEventId(
																	p.event
																)?.home
																	.shortName
																	? findEventId(
																			p.event
																	  )?.home
																			.shortName
																	: findEventId(
																			p.event
																	  )
																			?.home.name.slice(
																				0,
																				3
																			)
																			.toUpperCase()}
																<span className="text-custom-gray4 text-xs text-center font-normal font-bahnschrift">
																	x
																</span>
																{findEventId(
																	p.event
																)?.away
																	.shortName
																	? findEventId(
																			p.event
																	  )?.away
																			.shortName
																	: findEventId(
																			p.event
																	  )
																			?.away.name.slice(
																				0,
																				3
																			)
																			.toUpperCase()}
															</div>
															{findEventId(
																p.event
															)?.startAt && (
																<span className="text-custom-gray4 text-xs text-center font-normal font-bahnschrift">
																	-{' '}
																	{verifyDatePick(
																		(
																			findEventId(
																				p.event
																			)
																				?.startAt ??
																			''
																		).replace(
																			'Z',
																			''
																		)
																	)}
																</span>
															)}
														</div>

														<div className="text-text/heading-default pl-1.5 -mt-1 text-sm text-left font-normal font-bahnschrift">
															{p.pick.pick}
															<span className="pl-1 text-sm text-center font-normal font-bahnschrift leading-[12px]">
																{p.pick.pick > 0
																	? p.scout
																			.name
																	: p.scout
																			.name}
															</span>
														</div>
													</div>
												</div>

												<div className="w-24 flex flex-row gap-0.5 items-center">
													{p.pick.up && (
														<div
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																if (
																	p.pick.down
																) {
																	UpdatePicks(
																		pickSelectUp
																	);
																}
															}}
															className={
																'flex h-12 flex-col border border-custom-border items-center justify-center rounded-md' +
																(p.pick.down
																	? ' w-11'
																	: ' w-full') +
																(verifyPickEntry(
																	pickSelectUp
																)
																	? ' bg-primary'
																	: '')
															}
														>
															<div
																className={
																	'flex w-full flex-row items-center justify-center text-xs font-normal font-bahnschrift leading-3' +
																	(verifyPickEntry(
																		pickSelectUp
																	)
																		? ' text-white'
																		: ' text-text-paragraph')
																}
															>
																<RiArrowDropUpLine
																	size={30}
																/>
															</div>
															{Number(
																p.pick.factor
															) !== 1 && (
																<span
																	className={
																		'-mt-[11px] flex flex-row text-center justify-center items-center text-[10px] font-normal font-bahnschrift leading-3' +
																		(verifyPickEntry(
																			pickSelectUp
																		)
																			? ' text-white'
																			: ' text-text-paragraph')
																	}
																>
																	<RiFlashlightFill className="w-3.5 text-custom-yellow" />
																	{
																		p.pick
																			.factor
																	}
																	x
																</span>
															)}
														</div>
													)}
													{p.pick.down && (
														<div
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																if (p.pick.up) {
																	UpdatePicks(
																		pickSelectDown
																	);
																}
															}}
															className={
																'flex h-12 flex-col border border-custom-border items-center justify-center rounded-md' +
																(p.pick.up
																	? ' w-11'
																	: ' w-full') +
																(verifyPickEntry(
																	pickSelectDown
																)
																	? ' bg-primary'
																	: '')
															}
														>
															<div
																className={
																	'flex items-center justify-center text-text-paragraph text-[10px] font-normal font-bahnschrift leading-3' +
																	(verifyPickEntry(
																		pickSelectDown
																	)
																		? ' text-white'
																		: ' text-text-paragraph')
																}
															>
																<RiArrowDropDownLine
																	size={30}
																/>
															</div>
															{Number(
																p.pick.factor
															) !== 1 && (
																<span
																	className={
																		'-mt-[11px] flex flex-row text-center justify-center items-center text-[10px] font-normal font-bahnschrift leading-3' +
																		(verifyPickEntry(
																			pickSelectDown
																		)
																			? ' text-white'
																			: ' text-text-paragraph')
																	}
																>
																	<RiFlashlightFill className="w-3.5 text-custom-yellow" />
																	{
																		p.pick
																			.factor
																	}
																	x
																</span>
															)}
														</div>
													)}

													<div
														className={
															'flex w-8 h-6 flex-col border border-custom-border items-center justify-center rounded-full'
														}
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															RemovePick(p);
														}}
													>
														<RiCloseFill className="w-4" />
													</div>
												</div>
											</div>
										);
									})}
								</div>
								<div className="flex flex-row justify-between mt-2 gap-2">
									<div>
										<div className="flex flex-col gap-1">
											<div className="flex flex-row justify-between items-center">
												<span className="text-text/heading-default text-xs font-normal font-bahnschrift leading-3">
													Inscrições: R${' '}
													{CurrencyFormatter(
														Number(
															tournamentGroup.entryAmt
														)
													)}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-text/heading-default max-[415px]:text-xs text-xs text-left font-normal font-bahnschrift leading-[16px]">
													Qtd. Inscrições:{' '}
													{numberOfRegistrations}
												</span>
												<span className="text-text/heading-default max-[415px]:text-xs text-xs text-left font-normal font-bahnschrift leading-[16px]">
													Total: R${' '}
													{CurrencyFormatter(
														Number(
															tournamentGroup?.entryAmt
														) *
															numberOfRegistrations
													)}
												</span>
											</div>
											<div className="flex flex-row justify-start items-start gap-2 max-[415px]:gap-1 max-[375px]:gap-0.5">
												{[1, 2, 5, 10, 20, 50].map(
													(q, i) => (
														<div
															key={i}
															onClick={() => {
																setNumberOfRegistrations(
																	q
																);
															}}
															className={
																'w-9 h-9 max-[415px]:w-6 max-[415px]:h-6 cursor-pointer rounded-full border border-custom-border font-bahnschrift text-xs items-center justify-center flex' +
																(numberOfRegistrations ===
																q
																	? ' bg-primary text-white'
																	: ' text-text-paragraph')
															}
														>
															{q}
														</div>
													)
												)}
											</div>
										</div>
									</div>
									<div className="flex items-end">
										<div className="flex flex-col gap-1">
											<div className="flex flex-row justify-between items-center gap-2">
												<div className="flex flex-row items-center gap-1">
													{factor !== 1 && (
														<>
															<span className="text-text/heading-default text-xs font-normal font-bahnschrift leading-3">
																x{factor}
															</span>

															<Tooltip
																icon={
																	<RiInformationLine
																		color="#636363"
																		className="w-4 h-4"
																	/>
																}
																infoIcon="warning"
																title="Multiplicador"
																message={
																	<Multiple />
																}
															/>
														</>
													)}
												</div>

												<div className="flex flex-row items-center gap-1">
													<span className="text-text/heading-default text-xs font-normal font-bahnschrift leading-3">
														Torneios:{' '}
														{
															proposalTournaments.length
														}
													</span>
													<TooltipPick
														icon={
															<RiInformationLine
																color="#636363"
																className="w-4 h-4"
															/>
														}
														tournamentGroup={
															tournamentGroup
														}
														proposal={
															proposalTournaments
														}
													/>
												</div>
											</div>
											<div className="text-text/heading-default text-xs font-normal font-bahnschrift leading-3">
												Prêmio Máx: R${' '}
												{CurrencyFormatter(
													Number(totalPrizeAmt)
												)}
											</div>
										</div>
									</div>
								</div>
							</div>

							<Button
								className="w-full px-5 py-3"
								textCustom="text-custom-white"
								onClick={handleEnrollPick}
								disabled={
									picks.length === 0 ||
									errorProposal !== '' ||
									loadingProposal ||
									disableButton
								}
								isLoading={loadingProposal}
							>
								{errorProposal
									? errorProposal
									: error
									? error
									: 'Confirmar inscrição'}
							</Button>
						</div>
					</ModalPick>

					<ModalEnroll
						id="modalBalance"
						open={modal}
						onClose={closeModal}
					>
						<div className="flex flex-col gap-6 items-center">
							<Warning />

							<p className="w-80 text-sm text-center text-custom-gray2 font-bahnschrift">
								O saldo da sua carteira é de R${' '}
								{CurrencyFormatter(Number(currentBalance))},
								deseja efetuar um depósito?
							</p>

							<div className="flex flex-row justify-between gap-2 w-full">
								<Button
									onClick={closeModal}
									variant="help"
									textCustom="text-primary text-sm font-semibold"
									className="w-20 py-2 px-3.5"
								>
									Não
								</Button>
								<div className="w-full">
									<Button
										onClick={() => {
											closeModal();
											enrollsInfoModal(
												`/`,
												Number(
													tournamentGroup.entryAmt
												) *
													numberOfRegistrations -
													parseFloat(currentBalance),
												[],
												picks
											);
											navigate(`/wallet/deposit`);
										}}
										className={'w-full h-12'}
									>
										Efetuar depósito
									</Button>
								</div>
							</div>
						</div>
					</ModalEnroll>

					<ModalEnroll
						id="sucessModal"
						open={sucessModal}
						onClose={closeSucessModalEnroll}
						outsideClick
					>
						<div className="flex flex-col gap-6 items-center">
							<RiCheckDoubleFill className="w-12 h-12 text-[#039855]" />

							<p className="w-80 text-base text-center text-black font-bahnschrift">
								Inscrição concluida.
							</p>
						</div>
					</ModalEnroll>
				</>
			)}
		</PicksContext.Provider>
	);
};

export const usePicksContext = (): PicksContextType => {
	const context = useContext(PicksContext);
	if (context === undefined) {
		throw new Error('usePicksContext must be used within a PicksProvider');
	}
	return context;
};
